import { Asset, AssetDetails, Category } from '../../api';
import type { AiApiKey, AiImageResolution } from './aiApi';
import { extensionForMime } from './imageFormat';
import { AiImageMeta, aiSourceType } from './metadata/aiMetadata';

// An asset staged as a Magic Studio reference. Added to the basket from the
// catalog (card / detail panel / fullscreen) so it can be a full AssetDetails
// or a plain Asset. The first ref of a session is its primary "style anchor".
export type MagicPrimaryAsset = Asset | AssetDetails;

export type MagicStudioShape = {
  visible: boolean;
  // The "basket": references staged from the catalog while browsing. Holds the
  // same MagicRef union the studio consumes, so it can mix Asset Lab assets with
  // frames captured from the fullscreen viewer (video / 3D). Lives in the store
  // so it survives navigation across categories / folders / scopes, and is
  // capped at MAX_REFS so it never stages more than the studio can ingest.
  basket: MagicRef[];
  // Snapshot of the references a session was opened with — the basket at open
  // time, or empty when the studio is opened to paste/upload from scratch. The
  // first entry is the session's primary anchor. Kept separate from `basket` so
  // editing the basket afterwards doesn't mutate the live session.
  sessionRefs: MagicRef[];
  // The work in progress, stashed every time the modal closes so that closing
  // it — by accident or to look something up in the catalog — never throws away
  // what the user had going. Reopening resumes from here and the tray shows a
  // summary of it meanwhile. References are NOT part of it: the studio writes
  // them back into `basket` on close, which is both what the tray renders and
  // what the next open preloads. Null until the studio has produced anything,
  // and reset by "New session" / the tray's Clear.
  session: MagicSession | null;
};

// The studio state worth surviving a close. Everything else (generating, the
// error banner, the on-device progress, which panel was scrolled where) belongs
// to a single visit and is deliberately dropped.
export type MagicSession = {
  // The user-typed content only — the active template's system instruction is
  // re-applied from `templateId` on generate, exactly as during the session.
  prompt: string;
  templateId: string;
  templateLabel: string;
  // The model the session would generate with: a technical model name on the
  // backend path, a legacy studio id on the direct one. modelName() renders
  // both, so the tray can label it without knowing which path produced it.
  model: string;
  aspect: Aspect;
  resolution: Resolution;
  // The result on the canvas when the studio closed. Its image is a data URL,
  // so it stays displayable for as long as the tab lives.
  result: MagicResult | null;
};

// Whether a stashed session holds anything worth resuming — a prompt the user
// started typing or a result they haven't done anything with yet. References
// alone don't count: they live in the basket, which the tray already shows.
export function hasSessionWork(session: MagicSession | null): session is MagicSession {
  return !!session && (session.prompt.trim().length > 0 || !!session.result?.imageUrl);
}

// ─── Models ──────────────────────────────────────────────────────────
// Two model worlds coexist (MYUA-16447):
//
//  • MODELS below — the legacy frontend list keyed by an internal id ('nb',
//    'gpro', …). It only drives the *direct* Gemini path (MAGIC_BACKEND_ENABLED
//    off) and is still what old IndexedDB history records store.
//  • MODEL_CATALOG further down — display names (and the output sizes a model
//    accepts) keyed by the *technical* model name the backend expects
//    ('gemini-2.5-flash-image', 'gpt-image-1'). On the backend path the list of
//    usable models comes from the API key itself (AiApiKey.models); the catalog
//    only decorates it.
//
// Anything reading a stored model string (history cards, saved asset names, XMP
// metadata) must therefore accept both spellings — see modelName/modelSoftware.
export type MagicModel = {
  id: string;
  // Common / marketing name ("Nano Banana 2") — shown as the bold heading.
  name: string;
  // Technical model id ("gemini-3.1-flash-image-preview") — shown as the faint
  // subheading and is the single source of truth for the name the provider
  // actually calls (see geminiProvider). Absent for not-yet-available models.
  apiName?: string;
  // Short capability hint shown in the compact header pill.
  tag: string;
  disabled?: boolean;
  // Target output resolutions this model accepts (Gemini imageConfig.imageSize).
  // Absent for models that don't expose a size choice — the resolution control
  // is then hidden and no imageSize is sent.
  resolutions?: Resolution[];
  // Reachable only on the direct-to-Gemini path: the backend proxy has no price
  // entry for this model, so a generation through it would 400.
  directOnly?: boolean;
};

// ─── Output resolution ───────────────────────────────────────────────
// Target image size, passed through to Gemini's
// generationConfig.imageConfig.imageSize. Support varies per model, so the picker
// only offers the active model's `resolutions` (see modelResolutions).
//
// Supported on BOTH paths: the direct client sets imageConfig.imageSize itself,
// and the backend proxy now takes it as `resolution` on POST /ai/generations/
// (AiImageResolution). OpenAI has no equivalent — it takes a canvas `size`
// derived from the aspect — so the control is hidden for OpenAI models.
//
// The union is the backend's enum; `satisfies` keeps the two from drifting.
export const RESOLUTIONS = [
  '512',
  '1K',
  '2K',
  '4K',
] as const satisfies readonly AiImageResolution[];
export type Resolution = (typeof RESOLUTIONS)[number];
export const DEFAULT_RESOLUTION: Resolution = '1K';

export const MODELS: MagicModel[] = [
  {
    id: 'nb',
    name: 'Nano Banana',
    apiName: 'gemini-2.5-flash-image',
    tag: 'Fast · default',
    resolutions: ['1K', '2K'],
  },
  {
    // The preview spelling Google's API used for this model; the backend tariffs
    // it (and calls it) as plain 'gemini-3.1-flash-image', so on the proxy path it
    // arrives through the key under that name instead of this entry — which stays
    // here for the direct path and for old history records.
    id: 'nb2',
    name: 'Nano Banana 2',
    apiName: 'gemini-3.1-flash-image-preview',
    tag: 'Newest · sharper',
    resolutions: ['512', '1K', '2K', '4K'],
    directOnly: true,
  },
  {
    id: 'gpro',
    name: 'Nano Banana Pro',
    apiName: 'gemini-3-pro-image-preview',
    tag: 'Higher fidelity, slower',
    resolutions: ['1K', '2K', '4K'],
  },
  { id: 'future1', name: 'Imagen 4 Ultra', tag: 'Coming soon', disabled: true },
  { id: 'future2', name: 'Custom in-house', tag: 'Coming soon', disabled: true },
];

export const DEFAULT_MODEL_ID = MODELS[0].id;

// ─── Backend model catalog ───────────────────────────────────────────
// Display metadata for the technical model names the backend serves. Keyed by
// apiName because that is what travels in the generation payload, in the API
// key's `models` list and (from now on) in history records.
//
// The catalog never gates a model: which ones a user can actually run comes from
// AiApiKey.models, so a model the backend adds before the frontend knows about it
// still shows up (see describeModel's fallback) and — being Gemini — still gets a
// resolution choice (see modelResolutions). The catalog only sharpens both.
export type AiProviderId = 'gemini' | 'openai';
export type ModelInfo = {
  title: string;
  tag: string;
  provider: AiProviderId;
  // Output resolutions this model accepts as `resolution` on the backend path
  // (Gemini's imageConfig.imageSize) — set only where the model is known to
  // support fewer than all four. Absent means "we don't know": Gemini models then
  // fall back to the full enum rather than losing the control, OpenAI ones have
  // no size choice at all (they take a canvas `size` derived from the aspect).
  resolutions?: Resolution[];
};

export const MODEL_CATALOG: Record<string, ModelInfo> = {
  'gemini-2.5-flash-image': {
    title: 'Nano Banana',
    tag: 'Fast · default',
    provider: 'gemini',
    // 2.5 flash renders at 1K / 2K only — it has no 4K (nor 512) mode.
    resolutions: ['1K', '2K'],
  },
  'gemini-3.1-flash-image': { title: 'Nano Banana 2', tag: 'Newest · sharper', provider: 'gemini' },
  'gemini-3.1-flash-lite-image': {
    title: 'Nano Banana 2 Lite',
    tag: 'Fastest · cheapest',
    provider: 'gemini',
  },
  // The '-preview' spellings are what Google's own API used before the models
  // graduated; kept as aliases so history records written on the direct path (and
  // any key still reporting them) keep their pretty names.
  'gemini-3.1-flash-image-preview': {
    title: 'Nano Banana 2',
    tag: 'Newest · sharper',
    provider: 'gemini',
  },
  'gemini-3-pro-image-preview': {
    title: 'Nano Banana Pro',
    tag: 'Higher fidelity',
    provider: 'gemini',
    resolutions: ['1K', '2K', '4K'],
  },
  'gpt-image-1': { title: 'Image GPT', tag: 'OpenAI · gpt-image-1', provider: 'openai' },
};

// Display info for a model name; unknown names are shown as-is rather than
// hidden, so a newly tariffed backend model needs no frontend release.
export function describeModel(apiName: string, provider: AiProviderId): ModelInfo {
  return (
    MODEL_CATALOG[apiName] ?? {
      title: apiName,
      tag: provider === 'gemini' ? 'Gemini' : 'OpenAI',
      provider,
    }
  );
}

// What the user picks in the studio on the backend path: not a bare model, but
// a (stored key, model of that key) pair — the backend derives the provider from
// `api_key_id`, and the same model may be reachable through several keys
// (corporate + personal).
export type ModelSelection = {
  apiKeyId: number;
  provider: AiProviderId;
  // Technical model name; travels as `model` in the generation payload.
  model: string;
  // Key caption for the picker (`name` or, when unnamed, the provider).
  keyLabel: string;
};

// Every (key, model) pair the user can run, in the order the backend listed the
// keys. One option per model of each key — which is what makes "the provider is
// derived from the key" and "the same model reachable through two keys
// (corporate + personal)" both fall out for free.
export function modelSelections(keys: AiApiKey[]): ModelSelection[] {
  return keys.flatMap((key) =>
    key.models.map((model) => ({
      apiKeyId: key.id,
      provider: key.provider,
      model,
      keyLabel: key.name?.trim() || (key.provider === 'gemini' ? 'Gemini' : 'OpenAI'),
    })),
  );
}

// Whether two selections point at the same (key, model) pair.
export function sameSelection(a: ModelSelection | null, b: ModelSelection | null): boolean {
  return !!a && !!b && a.apiKeyId === b.apiKeyId && a.model === b.model;
}

// The resolutions a model offers (empty when it exposes no size choice, which
// hides the dropdown and sends no size at all). Accepts both spellings, like
// modelName: a legacy studio id on the direct path ('nb'), a technical model
// name on the backend path ('gemini-2.5-flash-image').
//
// `provider` is what the selected key reports (backend path). It matters for
// model names the catalog doesn't know: Gemini takes imageSize for every image
// model, so a freshly tariffed one gets the full list instead of silently losing
// the control — the backend still validates the value per model. Without a
// provider (direct path, history records) only the two tables are consulted.
export function modelResolutions(modelId: string, provider?: AiProviderId): Resolution[] {
  const legacy = MODELS.find((m) => m.id === modelId);
  if (legacy) return legacy.resolutions ?? [];
  const known = MODEL_CATALOG[modelId];
  if (known) return known.resolutions ?? (known.provider === 'gemini' ? [...RESOLUTIONS] : []);
  return provider === 'gemini' ? [...RESOLUTIONS] : [];
}

// Keep a chosen resolution valid when the model changes: keep it if the new
// model still supports it, otherwise fall back to its default (1K when offered,
// else the first option).
export function resolveResolution(
  modelId: string,
  current: Resolution,
  provider?: AiProviderId,
): Resolution {
  const options = modelResolutions(modelId, provider);
  if (options.includes(current)) return current;
  return options.includes(DEFAULT_RESOLUTION)
    ? DEFAULT_RESOLUTION
    : options[0] ?? DEFAULT_RESOLUTION;
}

// ─── Output options ──────────────────────────────────────────────────
// "Auto" lets the model choose the aspect (typically derived from the primary
// reference) — no aspectRatio is sent to the provider in that case.
export const ASPECTS = ['Auto', '1:1', '3:4', '4:3', '16:9', '9:16'] as const;
export type Aspect = (typeof ASPECTS)[number];
export const DEFAULT_ASPECT: Aspect = 'Auto';

// Matches the backend's MAX_REFERENCE_IMAGES = 8 (POST /ai/generations/ rejects
// more with 400 "Too many reference images"). Shared by the basket cap, the
// refs panel and the resolver's slice. The direct Gemini path caps itself lower
// (4, see geminiProvider) — that is deliberate and independent.
export const MAX_REFS = 8;

// ─── References ──────────────────────────────────────────────────────
// A reference passed (few-shot) to the model: either an Asset Lab asset or
// a locally provided image (clipboard paste / file upload / drag-and-drop).
export type MagicRef =
  | { kind: 'asset'; key: string; asset: MagicPrimaryAsset }
  | { kind: 'upload'; key: string; name: string; url: string };

export function assetRef(asset: MagicPrimaryAsset): MagicRef {
  return { kind: 'asset', key: `asset-${asset.id}`, asset };
}

let uploadSeq = 0;
export function uploadRef(name: string, url: string): MagicRef {
  uploadSeq += 1;
  return { kind: 'upload', key: `upload-${Date.now()}-${uploadSeq}`, name, url };
}

// References whose original file is at most this large are used as the original
// (best fidelity) — both sent to the model and shown in the studio. Larger
// originals fall back to the preview render: inlining multi-MB files bloats the
// model request and can exceed provider payload limits, so the cap is shared by
// the sender (imageResolver) and the UI.
export const MAX_ORIGINAL_BYTES = 5 * 1024 * 1024; // 5 MB

// The model ingests only flat raster stills, so only these original formats can
// be sent as-is. Everything else (PSD, GIF, video, WebM, 3D like FBX/OBJ, …)
// must go through the flattened preview render even when a download link exists.
const ORIGINAL_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg']);

// Whether the asset's original file is a format we can feed the model directly.
export function isOriginalImageFormat(asset: MagicPrimaryAsset): boolean {
  const ext = asset.type?.extension?.toLowerCase();
  return !!ext && ORIGINAL_IMAGE_EXTENSIONS.has(ext);
}

// The asset's original-file URL when it can be used directly — a supported
// raster format, downloadable, and small enough. Null otherwise (unsupported
// format, no download link, or over the cap), so callers fall back to the
// preview/thumbnail render.
export function assetOriginalUrl(asset: MagicPrimaryAsset): string | null {
  if (!isOriginalImageFormat(asset)) return null;
  return asset.download_url && asset.size <= MAX_ORIGINAL_BYTES ? asset.download_url : null;
}

// Asset byte endpoints (preview / thumbnail / download) come back from the
// backend as absolute URLs pointing at the backend host (e.g.
// "https://preprod.adsadvisor.io/api/..."). The download endpoint is
// authenticated; loaded cross-origin (plain fetch or <img src>) the browser
// can't attach the app's session cookie, so it fails. Rewriting it to a path
// relative to the app's own origin routes it through the same authenticated
// channel as every other API call — in dev the vite proxy (localhost:5173/api
// → backend), in prod the gateway that already serves /api. Only /api paths are
// rewritten; genuine third-party URLs (public signed previews) are left alone.
export function toAppApiUrl(rawUrl: string): string {
  try {
    const u = new URL(rawUrl, window.location.origin);
    if (u.origin !== window.location.origin && u.pathname.startsWith('/api/')) {
      return u.pathname + u.search;
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

export function assetImageUrl(asset: MagicPrimaryAsset): string | null {
  // Prefer the original so the studio shows exactly what reaches the model.
  // When it can't be used (unsupported format like PSD, missing, or oversize)
  // fall back to the flattened preview render — higher quality than the
  // thumbnail and what we actually send in that case — then the thumbnail last.
  const url = assetOriginalUrl(asset) ?? asset.preview?.url ?? asset.thumbnail_url ?? null;
  // The original is an authenticated endpoint — route it through the app origin
  // so the <img> request carries credentials (preview/thumbnail are untouched).
  return url ? toAppApiUrl(url) : null;
}

export function refImageUrl(ref: MagicRef): string | null {
  return ref.kind === 'asset' ? assetImageUrl(ref.asset) : ref.url;
}

// Small still for chips / basket thumbnails. Unlike assetImageUrl — which leads
// with the original / preview render so the studio shows exactly what reaches
// the model — this always prefers the raster `thumbnail_url`: it's a real image
// for every asset type, whereas `preview` can itself be a video / 3D stream
// (e.g. an .mp4 preview) that an <img> can't render — the cause of the blank
// white thumbnail for video / 3D assets.
export function assetThumbUrl(asset: MagicPrimaryAsset): string | null {
  const url = asset.thumbnail_url ?? asset.preview?.url ?? assetOriginalUrl(asset) ?? null;
  return url ? toAppApiUrl(url) : null;
}

export function refThumbUrl(ref: MagicRef): string | null {
  return ref.kind === 'asset' ? assetThumbUrl(ref.asset) : ref.url;
}

export function refName(ref: MagicRef): string {
  return ref.kind === 'asset' ? ref.asset.name : ref.name;
}

export function refAssetId(ref: MagicRef): number | null {
  return ref.kind === 'asset' ? ref.asset.id : null;
}

export function refExtension(ref: MagicRef): string {
  if (ref.kind === 'asset') return (ref.asset.type?.extension || 'FILE').toUpperCase();
  const ext = ref.name.includes('.') ? ref.name.split('.').pop() : '';
  return (ext || 'IMG').toUpperCase();
}

// ─── Prompt modifiers ────────────────────────────────────────────────
// Quick "+" chips that append a phrase to the prompt's content. Each carries a
// short description, surfaced on hover, of how it nudges the generation.
export type PromptModifier = { label: string; description: string };

export const PROMPT_MODIFIERS: PromptModifier[] = [
  {
    label: 'high detail',
    description: 'Pushes the model toward crisp, fine detail and sharper textures.',
  },
  {
    label: 'soft top-down light',
    description: 'Lights the scene softly from above with gentle, diffused shadows.',
  },
  {
    label: 'neutral background',
    description: 'Places the subject on a plain, distraction-free backdrop.',
  },
  {
    label: 'matte painting',
    description: 'Renders in a painterly matte-painting look rather than photoreal.',
  },
  {
    label: 'tileable',
    description: 'Makes the result seamlessly repeatable as a texture tile.',
  },
  {
    label: '3/4 hero pose',
    description: 'Frames the subject from a three-quarter hero angle.',
  },
];

// ─── Templates ───────────────────────────────────────────────────────
// A template is a reusable "mode": its prompt acts as a system instruction
// applied on top of whatever the user types in the prompt field (the content
// part). In a real Asset Lab these would live on the server and be
// admin-editable; for now they start from the defaults below and the user's own
// edits/additions are kept per-device (see templatesStore.ts). "No template" is
// the neutral mode with no system instruction.
export type MagicTemplate = {
  id: string;
  label: string;
  // Short description — shown on hover over the template row (kept out of the
  // list itself so more templates fit on screen).
  hint: string;
  // System instruction prepended to the user's content on generation. Empty
  // for the "No template" mode.
  prompt: string;
  presetRefs: string[];
  custom?: boolean;
  // On-device post-processing applied to the generated image after this
  // template runs, in order: upscale first, then background removal. Each is
  // independent. Edited in the template form.
  postUpscale?: boolean;
  postRemoveBg?: boolean;
};

// The neutral "No template" mode: no system instruction, the prompt field is
// everything. Also the target the template badge resets to. The id stays
// 'custom' — history records store it, so renaming it would orphan them.
export const CUSTOM_TEMPLATE_ID = 'custom';

export function assetBaseName(asset: MagicPrimaryAsset | null | undefined): string {
  return asset?.name?.replace(/\.[^.]+$/, '') || 'asset';
}

// ─── Save-to-assets helpers ──────────────────────────────────────────
// Human / marketing name for a model string. Accepts BOTH spellings, and must
// keep doing so: history records written before MYUA-16447 hold legacy ids
// ('nb', 'gpro'), everything written since holds the technical model name
// ('gemini-2.5-flash-image', 'gpt-image-1'). Unknown strings pass through.
//
// The single source of truth for the History card subtitle and the saved
// asset's filename suffix.
export function modelName(id: string): string {
  const legacy = MODELS.find((m) => m.id === id);
  if (legacy) return legacy.name;
  return MODEL_CATALOG[id]?.title ?? id;
}

// The application written into a saved/downloaded file's metadata as the
// creating tool — the model/engine is appended in parentheses by aiMetaFor.
const MAGIC_STUDIO_APP = 'Asset Lab Magic Studio';

// The model/engine token: marketing name + the precise API model when known,
// e.g. "Nano Banana 2 / gemini-3.1-flash-image-preview". ASCII-only separator —
// a non-ASCII one (e.g. "·") renders as mojibake ("Â·") in viewers that read the
// XMP as Latin-1 rather than its actual UTF-8.
//
// Like modelName, accepts both a legacy id and a technical model name, so XMP
// metadata stays correct for old and new generations alike:
//   modelSoftware('nb')          → "Nano Banana / gemini-2.5-flash-image"
//   modelSoftware('gpt-image-1') → "Image GPT / gpt-image-1"
export function modelSoftware(id: string): string {
  const legacy = MODELS.find((x) => x.id === id);
  if (legacy) return legacy.apiName ? `${legacy.name} / ${legacy.apiName}` : legacy.name;
  const known = MODEL_CATALOG[id];
  return known ? `${known.title} / ${id}` : id;
}

// The AI-provenance metadata embedded into a result's file (xmp:CreatorTool /
// tiff:Software + IPTC DigitalSourceType). The software is one string carrying
// both the tool and the engine: "Asset Lab Magic Studio (<engine>)". `source`
// marks the on-device tools (background removal / upscale) — edits via a local
// engine rather than a cloud generation. See metadata/aiMetadata.ts.
export function aiMetaFor(modelId: string, source?: 'removeBg' | 'upscale'): AiImageMeta {
  const engine =
    source === 'removeBg'
      ? 'Background removal'
      : source === 'upscale'
        ? 'Upscale 2x'
        : modelSoftware(modelId);
  return { software: `${MAGIC_STUDIO_APP} (${engine})`, sourceType: aiSourceType(source) };
}

// True only when the asset carries the full details we need to pick a save
// target without asking the user — a non-null category (its folder node) and a
// project. Plain `Asset` (staged from a grid card / fullscreen) has neither, so
// the target can only be derived from refs added via the detail-panel sidebar,
// which stage the full AssetDetails.
export function assetHasCategory(
  asset: MagicPrimaryAsset,
): asset is AssetDetails & { category: Category } {
  return (
    'category' in asset &&
    (asset as AssetDetails).category != null &&
    'project' in asset &&
    (asset as AssetDetails).project != null
  );
}

// The base (extension-stripped) name of a reference, used to prefill the saved
// asset's name. Accepts both live MagicRefs and persisted history refs (they
// share the asset / name shape).
type RefBaseLike = { kind: 'asset'; asset: MagicPrimaryAsset } | { kind: 'upload'; name: string };
export function firstRefBaseName(ref: RefBaseLike | null | undefined): string {
  if (!ref) return 'magic-studio';
  if (ref.kind === 'asset') return assetBaseName(ref.asset);
  return ref.name.replace(/\.[^.]+$/, '') || 'magic-studio';
}

// The default name for a generation saved to the Asset Lab:
// `<firstRefBase>_AI_<model>_<YYYY-MM-DD>.<ext>`. The model name is squeezed to
// alphanumerics so it's a clean filename token (e.g. "Nano Banana 2" → "NanoBanana2").
// The extension follows the image's own mime type — models don't all answer PNG
// (Nano Banana returns JPEG) — and falls back to png when it isn't known yet.
export function aiAssetName(
  baseName: string,
  modelId: string,
  date: Date,
  mime?: string | null,
): string {
  const model = modelName(modelId).replace(/[^a-z0-9]+/gi, '') || 'AI';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${baseName}_AI_${model}_${y}-${m}-${d}.${extensionForMime(mime) ?? 'png'}`;
}

export function defaultTemplates(): MagicTemplate[] {
  return [
    {
      id: CUSTOM_TEMPLATE_ID,
      label: 'No template',
      hint: 'No system instruction — write your own prompt from scratch.',
      prompt: '',
      presetRefs: ['primary'],
    },
    {
      id: 'inpaint',
      label: 'Inpaint',
      hint: 'Fill in or complete a part of the image.',
      prompt:
        'Using the attached reference image, redraw and complete the missing, masked or unfinished area so it blends seamlessly with the rest. Keep everything else identical — same subject, lighting, palette, perspective and style.',
      presetRefs: ['primary'],
    },
    {
      id: 'bg',
      label: 'Replace background',
      hint: 'Keep the subject, swap the background.',
      prompt:
        'Keep the main subject from the attached reference image exactly as it is — same pose, proportions, lighting and details. Replace only the background according to the description below.',
      presetRefs: ['primary'],
    },
    {
      id: 'chroma',
      label: 'Green screen (Chroma key)',
      hint: 'Put the subject on a solid green backdrop.',
      prompt:
        'Place the main subject from the attached reference image on a solid chroma-key green background (#00B140), evenly lit with no spill or shadows on the backdrop and clean edges suitable for keying. Keep the subject itself unchanged.',
      presetRefs: ['primary'],
    },
    {
      id: 'refstyle',
      label: 'Create with reference style',
      hint: 'Generate new content in the reference style.',
      prompt:
        'Create a new image that matches the visual style of the attached reference image(s): the same lighting, colour palette, materials, rendering technique and overall art direction. The subject to create is described below.',
      presetRefs: ['primary'],
    },
    {
      // Pure restoration: the model re-renders the image at higher fidelity
      // without touching the composition — no post-processing attached, so the
      // background comes back as it was. Upscaling and cutting out the
      // background stay available as explicit on-device tools (and as
      // after-generation checkboxes the user can enable per template).
      id: 'enhance',
      label: 'Improve Quality',
      hint: 'Clean up artifacts and crisp up the edges without changing the image.',
      prompt:
        'Enhance and clean up the attached reference image WITHOUT redrawing it. This is a faithful, pixel-accurate restoration — NOT a new illustration. Keep the subject EXACTLY identical: the same pose, gesture, hand and limb positions, head tilt, facial expression, left/right orientation (do NOT flip or mirror), the same proportions, silhouette, outline, line work, shading, colour palette, art style and composition. Do not move, re-pose, redraw, reinterpret, recolour or restyle any part of it, and do not change which way it faces. Keep the background exactly as it is — do not replace, remove, blur or restyle it, and do NOT draw a checkerboard, transparency pattern or any fake "transparent" texture. Only improve technical quality: remove compression artifacts, JPEG blocking, jagged/aliased edges, blur and noise, and make the existing edges and details crisp and clean.',
      presetRefs: ['primary'],
    },
  ];
}

// Combines a template's system instruction with the user-typed content into the
// final prompt sent to the model. Either part may be empty ("No template" has no
// system instruction); they are joined as separate paragraphs when both exist.
export function composePrompt(tpl: MagicTemplate | undefined, userText: string): string {
  const system = (tpl?.prompt ?? '').trim();
  const user = userText.trim();
  if (system && user) return `${system}\n\n${user}`;
  return system || user;
}

// ─── Generation results ──────────────────────────────────────────────
// Token usage reported by the provider for a generation, when available
// (Gemini's usageMetadata). Each field is null when the provider doesn't
// report it. Used for product logging, not shown in the UI.
export type MagicUsage = {
  promptTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  // What the generation actually cost, in USD, as billed by the backend proxy
  // (`usage.cost_usd`). Optional: the direct-Gemini path and on-device tools
  // don't know it, and history records written before MYUA-16447 don't have it.
  costUsd?: number | null;
};

export type MagicResult = {
  id: string;
  seed: number;
  aspect: Aspect;
  format: string;
  // URL of the generated image. For the first iteration the external model
  // API is faked, so this is seeded from the primary asset's image.
  imageUrl: string | null;
  // When the result came from an on-device tool (background removal / upscale)
  // rather than cloud generation. Drives the result-viewer label and lets the
  // viewer paint a transparency checkerboard for cut-out (alpha) images.
  // Absent for normal generation.
  source?: 'removeBg' | 'upscale';
  // Decoded byte size of imageUrl, when known — shown as the result's weight.
  // Pixel dimensions are read from the <img> on load (see ResultViewer), so they
  // aren't stored here.
  bytes?: number;
  // Token usage reported by the cloud provider, when available. Absent for
  // on-device tools and the demo provider.
  usage?: MagicUsage;
};

// Decoded byte size of a base64 data URL (e.g. "data:image/png;base64,…"),
// without re-decoding the bytes: base64 encodes 3 bytes per 4 chars, minus the
// "=" padding. Returns null for non-data / non-base64 URLs.
export function dataUrlByteSize(url: string): number | null {
  const comma = url.indexOf(',');
  if (!url.startsWith('data:') || comma < 0) return null;
  if (!/;base64/i.test(url.slice(0, comma))) return null;
  const b64 = url.slice(comma + 1);
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}
